import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Rating,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  Archive,
  Visibility,
  LocalBar,
} from '@mui/icons-material';
import { Bottle, BottleType } from '../../../domain/entities';
import { getBottleTypeColor } from '../../../core/theme/theme';
import { BOTTLE_TYPES_LABELS } from '../../../core/config/constants';

interface BottleCardProps {
  bottle: Bottle;
  onEdit?: (bottle: Bottle) => void;
  onDelete?: (bottle: Bottle) => void;
  onArchive?: (bottle: Bottle) => void;
  onView?: (bottle: Bottle) => void;
}

export const BottleCard: React.FC<BottleCardProps> = ({
  bottle,
  onEdit,
  onDelete,
  onArchive,
  onView,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const getBottleImage = () => {
    if (bottle.imageUrl) {
      return bottle.imageUrl;
    }
    return undefined;
  };

  return (
    <Card
      sx={{
        maxWidth: 280,
        m: 1,
        borderRadius: 1,
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
      onClick={() => onView?.(bottle)}
    >
      <Box sx={{ position: 'relative' }}>
        {getBottleImage() ? (
          <CardMedia
            component="img"
            height="200"
            image={getBottleImage()}
            alt={bottle.name}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: getBottleTypeColor(bottle.type),
              color: 'white',
            }}
          >
            <LocalBar sx={{ fontSize: 60, opacity: 0.7 }} />
          </Box>
        )}

        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0,0,0,0.5)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.7)',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e);
          }}
        >
          <MoreVert />
        </IconButton>

        <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
          <Chip
            label={BOTTLE_TYPES_LABELS[bottle.type as keyof typeof BOTTLE_TYPES_LABELS]}
            size="small"
            sx={{
              backgroundColor: getBottleTypeColor(bottle.type),
              color: 'white',
              fontWeight: 500,
            }}
          />
        </Box>

        {bottle.isGift && (
          <Box sx={{ position: 'absolute', top: 40, left: 8 }}>
            <Chip
              label="🎁 Cadeau"
              size="small"
              sx={{
                backgroundColor: 'secondary.main',
                color: 'white',
              }}
            />
          </Box>
        )}

        {bottle.isPrivate && (
          <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
            <Chip
              icon={<Visibility sx={{ fontSize: 16 }} />}
              label="Privé"
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: 'text.primary',
              }}
            />
          </Box>
        )}
      </Box>

      <CardContent sx={{ p: 2 }}>
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 600,
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {bottle.name}
        </Typography>

        {bottle.year && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Millésime {bottle.year}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Rating
            value={bottle.rating}
            readOnly
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            ({bottle.rating}/5)
          </Typography>
        </Box>

        {bottle.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {bottle.description}
          </Typography>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Ajouté le {new Date(bottle.createdAt).toLocaleDateString('fr-FR')}
        </Typography>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {onView && (
          <MenuItem onClick={() => handleAction(() => onView(bottle))}>
            <Visibility sx={{ mr: 1 }} />
            Voir les détails
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={() => handleAction(() => onEdit(bottle))}>
            <Edit sx={{ mr: 1 }} />
            Modifier
          </MenuItem>
        )}
        {onArchive && (
          <MenuItem onClick={() => handleAction(() => onArchive(bottle))}>
            <Archive sx={{ mr: 1 }} />
            Archiver
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem
            onClick={() => handleAction(() => onDelete(bottle))}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1 }} />
            Supprimer
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};